import * as _ from "lodash";
import { createSlice } from "@reduxjs/toolkit";
import {
  addRequester,
  countRequesterOnline,
  editRequester,
  listRequesterOffline,
  listRequesterOnline,
  readRequester,
  readRequesterSession,
  removeRequester,
} from "./thunkAction";

const initialState = {
  requester: {},
  requesters: {},
  requesterOnline: [],
  requesterOffline: [],
  localFormRequester: null,
  totalOnline: 0,
  loading: false,
  error: null,
};

const requesterSlice = createSlice({
  name: "requesterManage",
  initialState,
  reducers: {
    clearRequester(state) {
      state.requesters = {};
      state.requester = {};
    },
    setRequester(state, action) {
      state.requester = _.get(action, "payload", {});
    },
    resetProfileError(state) {
      state.error = null;
    },
    getLocalFormRequester(state, action) {
      const domain = _.get(action, "payload", null);
      const raw = localStorage.getItem(`${domain}_local_form_requester`);

      try {
        const formData = JSON.parse(raw);

        if (formData) {
          state.localFormRequester = formData;
        }
      } catch {
        localStorage.removeItem(`${domain}_local_form_requester`);
        state.localFormRequester = null;
      }
    },
    setLocalFormRequester(state, action) {
      const data = _.get(action, "payload", {});
      const formData = _.get(data, "formData", null);
      const domain = _.get(data, "domain", "");

      if (!formData) {
        return;
      }
      state.localFormRequester = formData;
      localStorage.setItem(
        `${domain}_local_form_requester`,
        JSON.stringify(formData)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // listRequesterOnline
      .addCase(listRequesterOnline.pending, (state) => {
        state.loading = true;
      })
      .addCase(listRequesterOnline.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        const requesters = _.get(action, "payload", []);
        const sorted = _.orderBy(
          requesters,
          (req) => {
            const timeAttr = req.attributes.find(
              (attr) => attr.key === "timeOnline"
            );
            return timeAttr ? timeAttr.value : Infinity;
          },
          ["asc"]
        );
        state.requesterOnline = sorted;
      })
      .addCase(listRequesterOnline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // listRequesterOffline
      .addCase(listRequesterOffline.pending, (state) => {
        state.loading = true;
      })
      .addCase(listRequesterOffline.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        const requesters = _.get(action, "payload", []);

        const [online, offline] = _.partition(requesters, (req) =>
          _.some(req.attributes, { key: "isOnline", value: true })
        );

        const sortedOnline = _.orderBy(
          online,
          [
            (req) => {
              const attr = req.attributes.find((a) => a.key === "timeOnline");
              return attr ? attr.value : Infinity;
            },
          ],
          ["asc"]
        );

        const sortedOffline = _.orderBy(
          offline,
          [
            (req) => {
              const attr = req.attributes.find((a) => a.key === "timeOnline");
              return attr ? attr.value : -Infinity;
            },
          ],
          ["desc"]
        );

        const sorted = [...sortedOnline, ...sortedOffline];

        state.requesterOffline = sorted;
      })
      .addCase(listRequesterOffline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // countRequesterOnline
      .addCase(countRequesterOnline.pending, (state) => {
        state.loading = true;
      })
      .addCase(countRequesterOnline.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.totalOnline = _.get(action, "payload", 0);
      })
      .addCase(countRequesterOnline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // addRequester
      .addCase(addRequester.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRequester.fulfilled, (state, action) => {
        state.loading = false;
        state.requester = _.get(action, "payload", {});
      })
      .addCase(addRequester.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // editRequester
      .addCase(editRequester.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editRequester.fulfilled, (state, action) => {
        state.loading = false;
        state.requester = _.get(action, "payload", {});
      })
      .addCase(editRequester.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // removeRequester
      .addCase(removeRequester.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRequester.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(removeRequester.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // readRequester
      .addCase(readRequester.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(readRequester.fulfilled, (state, action) => {
        state.loading = false;
        const requester = _.get(action, "payload", {});
        const requesterId = _.get(requester, "_id");
        state.requester = requester;
        if (requesterId) {
          state.requesters[requesterId] = requester;
        }
      })
      .addCase(readRequester.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // readRequesterSession
      .addCase(readRequesterSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(readRequesterSession.fulfilled, (state, action) => {
        state.loading = false;
        const requester = _.get(action, "payload", {});
        const requesterId = _.get(requester, "_id");
        if (requesterId) {
          state.requesters[requesterId] = requester;
        }
      })
      .addCase(readRequesterSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const requesterReducer = requesterSlice.reducer;

export const requesterActions = requesterSlice.actions;
